import { z } from 'zod';

import type { EvalResult } from '../../env.ts';
import { type AstInput, nodePos } from '../../normalize.ts';
import { type ColumnRefInArg, opMeta, type PropagateSchemaCtx, type SpecialCtx } from '../../op-meta.ts';
import { kwargPath, mapValuePath, paramPath } from '../../path.ts';
import { materializedToRows } from '../../runtime/arquero.ts';
import { groupRowsBy } from '../../runtime/partition.ts';
import { astArg } from '../../schema/ast-codec.ts';
import { type ColumnNameAst, ColumnNameAstSchema, columnNameAstString } from '../../schema/keyword.ts';
import { type ColumnType, type Schema, schemaFromEntries } from '../../schema/types.ts';
import { type Cell, type Columns, MaterializedDataset, synthesizeIdentity } from '../../value.ts';
import {
  type AggPairsMapEntry,
  aggContextScope,
  aggInputColumn,
  aggOutputEntries,
  aggPairsMapSchema,
  compileAggCallReducer,
} from './agg-funcs.ts';
import { requireColumns, resolveDsArg, vectorColRefs } from './helpers.ts';

/**
 * `(aggregate ds :group-by [:r…] :pairs {:out (agg-call …) …})` —
 * group-then-reduce. Rows partition by the distinct value tuples of
 * `:group-by` columns; within each group, every entry of `:pairs`
 * runs against the group's full row set and emits one output cell.
 * The output has one row per group with the `:group-by` columns first,
 * then the named aggregate columns in declaration order.
 *
 * `:group-by` is OPTIONAL — omit for a global rollup that emits
 * exactly one row. Each `:group-by` entry is a column-name AST (keyword
 * typical, quoted string for special chars).
 *
 * Every agg-call sees the FULL original row of every group member, so
 * keyword col-refs in the agg (e.g. `(sum :revenue)`) resolve against
 * the input dataset's columns — symmetric with `pivot` / `time-bucket`.
 *
 * Errors:
 *
 *   - First arg not a `Dataset` → `TypeMismatchError`.
 *   - A `:group-by` name isn't a column on `ds.schema` →
 *     `UnknownColumnRefError`.
 *   - `:pairs` value names an op that isn't an agg func → `UnknownOpError`.
 *   - `:pairs` is empty / not a map literal → schema-validation `ShapeError`.
 *
 * A `LazyDataset` input is forced via `ctx.materialize` before the
 * reduce runs.
 */
export const aggregate = z
  .object({
    name: z.literal('aggregate'),
    params: z.tuple([astArg('<dataset>')]),
    'group-by': z.array(ColumnNameAstSchema).optional(),
    pairs: aggPairsMapSchema(':pairs'),
  })
  .describe(
    "Group rows by `:group-by` columns (omit for global rollup) and emit one row per group with the `:pairs` map's aggregates ({:out (agg-call …)}).",
  )
  .register(opMeta, {
    // Special evaluation: the `:pairs` map's values are agg-call ASTs
    // whose op (`sum`, `mean`, …) lives in `aggMeta`, NOT `opMeta` —
    // eager dispatch would `evalNode` them and surface an
    // UnknownOpError before apply runs. The dataset positional is
    // evaluated by hand via `ctx.evalNode`; `:group-by` arrives as
    // ColumnNameAst entries (keyword / string), extracted with
    // `columnNameAstString`.
    evaluation: 'special',
    effect: 'pure',
    childScopes: [aggContextScope],
    example: { expr: '(-> sales (aggregate :group-by [:region] :pairs {:tot (sum :revenue) :n (count)}))' },
    apply: async (data, ctx: SpecialCtx): Promise<EvalResult> => {
      const dsAst = (data.params as readonly [AstInput])[0];
      const { value: dsValue } = await ctx.evalNode(dsAst, ctx.env, paramPath(ctx.path, 0));
      const concrete = await resolveDsArg(dsValue, '(aggregate ds :group-by […] :pairs {…})', ctx);

      const groupByCols = ((data['group-by'] as readonly ColumnNameAst[] | undefined) ?? []).map(columnNameAstString);
      requireColumns(groupByCols, concrete.schema, (idx) =>
        nodePos(ctx.spans, paramPath(kwargPath(ctx.path, 'group-by'), idx)),
      );

      const pairs = data.pairs.$map as readonly AggPairsMapEntry[];
      const compiledPairs = await Promise.all(
        pairs.map(async ({ k, v }, i) => ({
          outName: k,
          reduce: await compileAggCallReducer(v, mapValuePath(kwargPath(ctx.path, 'pairs'), k, i), ctx.spans),
        })),
      );

      const rows = materializedToRows(concrete);
      const groups = groupRowsBy(rows, groupByCols);
      // Emit output columns directly. Building one row record per group via
      // Object.fromEntries only to transpose them straight back through
      // rowsToColumns is pure allocation churn — go column-major now. Order
      // is group-by columns first, then agg pairs (Object.fromEntries keeps
      // insertion order), matching the previous outColOrder.
      const groupByColumns = groupByCols.map(
        (c) => [c, groups.map(({ representativeRow }) => representativeRow[c] as Cell)] as const,
      );
      const aggColumns = compiledPairs.map(
        ({ outName, reduce }) => [outName, groups.map(({ rows: groupRows }) => reduce(groupRows) as Cell)] as const,
      );
      const outCols: Columns = Object.fromEntries([...groupByColumns, ...aggColumns]);
      // SYNTHESIS: each output row keeps its group's single instance
      // identity IFF `:group-by` includes both `space` and `externalId`
      // (then both survive verbatim as group-by columns); else undefined.
      return {
        value: new MaterializedDataset(
          outCols,
          propagateAggregateSchema(concrete.schema, groupByCols, pairs),
          synthesizeIdentity(outCols, groupByCols, concrete.schema),
        ),
        env: ctx.env,
      };
    },
    dataset: {
      // Static schema = group-by cols (typed from input) + agg pair
      // outputs (typed via `aggOutputEntries` against the input
      // schema). Both are knowable pre-eval; `propagateAggregateSchema`
      // is the SSOT used by both apply and the engine hook.
      propagateSchema: (data, ctx: PropagateSchemaCtx): Schema | undefined => {
        const kwargs = data as { readonly 'group-by'?: unknown; readonly pairs?: unknown };
        const groupBy = vectorColRefs(kwargs['group-by'], '').map((r) => r.name);
        const pairsMap = (kwargs.pairs as { readonly $map?: readonly AggPairsMapEntry[] } | undefined)?.$map;
        if (pairsMap === undefined) {
          return undefined;
        }
        return propagateAggregateSchema(ctx.inputSchema, groupBy, pairsMap);
      },
      // `:group-by` entries are id-col USAGES. Each agg-pair's call
      // references at most one input col (extracted via
      // `aggInputColumn`). `:pairs` output keys are definitions
      // (excluded).
      referencedColumns: (data, basePath): readonly ColumnRefInArg[] => {
        const kwargs = data as { readonly 'group-by'?: unknown; readonly pairs?: unknown };
        const groupRefs = vectorColRefs(kwargs['group-by'], kwargPath(basePath, 'group-by'));
        const pairsMap = (kwargs.pairs as { readonly $map?: readonly AggPairsMapEntry[] } | undefined)?.$map ?? [];
        const pairRefs = pairsMap.flatMap(({ k, v }, i) => {
          const name = aggInputColumn(v as { readonly params?: readonly unknown[] });
          return name === undefined
            ? []
            : [{ name, path: `${mapValuePath(kwargPath(basePath, 'pairs'), k, i)}/params/0` }];
        });
        return [...groupRefs, ...pairRefs];
      },
    },
  });

/**
 * Build the post-aggregate schema. Group-by columns keep their input
 * types; each agg-pair output is typed via the shared `aggOutputEntries`
 * SSOT against the input schema (so a known view/source type survives
 * aggregation). Group-by names have already been validated against the
 * input schema by apply, so the lookups here are total.
 */
const propagateAggregateSchema = (
  inputSchema: Schema,
  groupByCols: readonly string[],
  pairs: readonly AggPairsMapEntry[],
): Schema => {
  const groupEntries = groupByCols.map((c) => [c, inputSchema.get(c) as ColumnType] as const);
  // Adapt map entries to the (name, params[<agg-call>]) shape expected
  // by `aggOutputEntries` — its existing input shape predates the map
  // surface; adapting at the call site keeps the SSOT untouched.
  const aggPairs = pairs.map(({ k, v }) => ({ name: k, params: [v] as readonly unknown[] }));
  return schemaFromEntries([...groupEntries, ...aggOutputEntries(aggPairs, inputSchema)]);
};
